import React, { useState } from "react";
import toast from "react-hot-toast";
import { useDeleteAccount } from "../../hooks/useDeleteAccount";

export default function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  const { mutate } = useDeleteAccount({
    onSuccessCallback: async (data) => {
      toast.success(data.message || "Account deletion request submitted");
      setEmail("");
      setLoading(false);
      setIsChecked(false);
    },

    onErrorCallback: (errorMsg) => {
      setLoading(false);
      toast.error(errorMsg);
    },
  });

  const handleDelete = () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const mobilePattern = /^(\+?\d{1,3}[- ]?)?\d{10}$/;

    const isValid = emailPattern.test(email) || mobilePattern.test(email);

    if (!isValid) {
      toast.error(
        "Enter a valid email or mobile number"
      );
      return;
    }

    if (!isChecked) {
      toast.error("Please check the confirmation box");
      return;
    }
    setLoading(true);
    mutate({
      value: email,
    });
  };

  return (
    <div className="w-full  flex items-center justify-center ">
      <div className="2xl:container w-full max-w-[800px]  pt-12 md:pt-[72px] md:px-20 p-4 ">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-4">
            <svg
              className="w-10 h-10 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Delete Account
          </h1>
          <p className="text-gray-600 text-sm md:text-base">
            Request permanent deletion of your account and data
          </p>
        </div>

        {/* Warning Box */}
        <div className=" text-left bg-red-50 border-2 border-red-200 rounded-brand p-4 md:p-6 mb-6">
          <div className="flex items-start gap-3">
            <svg
              className="w-6 h-6 text-red-600 flex-shrink-0 mt-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="flex-1">
              <h3 className="text-red-700 font-semibold text-lg mb-3">
                Important Warning
              </h3>
              <ul className="space-y-2 text-red-700 text-sm">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>This action is permanent and cannot be undone</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    All your data will be permanently deleted within 7 days
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    This includes messages, contacts, and account information
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>You will lose access to all services immediately</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Any active subscriptions will be cancelled</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="text-left bg-white border border-gray-200 rounded-brand shadow-sm p-6 md:p-8">
          {/* Email Input */}
          <div className="mb-6">
            <label className="block text-gray-900 font-medium mb-2">
              Email Address / Mobile Number{" "}
              <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              {/* <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg> */}
              <input
                type="email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your registered email or mobile number"
                className="w-full bg-white border border-gray-300 rounded-brand pl-4 pr-4 py-3 text-gray-900 placeholder-gray-400 outline-none  "
              />
            </div>
          </div>

          {/* Confirmation Checkbox */}
          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-red-600  cursor-pointer"
              />
              <span className="text-sm text-gray-700 group-hover:text-gray-900 transition">
                I understand that this action is permanent and all my data will
                be deleted within 7 days. I won't be able to recover my account
                or any data after deletion.
              </span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleDelete}
              disabled={!email || !isChecked}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-brand transition flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              {loading ? "Processing..." : "Delete My Account Permanently"}
            </button>

            <button
              onClick={() => window.history.back()}
              className="w-full bg-white hover:bg-gray-50 text-gray-700 hover:text-gray-900 font-medium py-3 px-6 rounded-brand border border-gray-300 hover:border-gray-400 transition"
            >
              Cancel and Return to Safety
            </button>
          </div>
        </div>

        {/* Support Contact */}
        <div className="text-center mt-6">
          <p className="text-gray-600 text-sm">
            Need help? Contact our support team at{" "}
            <a
              href="mailto:admin@socialspectrum.org"
              className="text-red-600 hover:text-red-700 transition"
            >
              admin@socialspectrum.org
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
